import axios from 'axios';
import {
  getParts, groupOfCategory, isHardwareOrTool, setPartSize,
  type PartItem, type PartSize,
} from './partsRun';
import { measurableKeys } from './formatUnits';
import { getSubmodels } from './listingStore';
import { readState, writeState } from './docState';
import { RUN_MODEL } from './models';
import { measureImages } from './imageSize';
import { bestRead, scaleDerivation, scaleEstimates, scaleJson, scaledSize } from './sizeFromScale';
import type { Dims } from './meshAnnotations';

// Phase 2 helper: how big each part really is, read off the listing's dimension drawing.
//
// It has to come from the LISTING and not from the manual, and that is the whole reason
// this exists. A manual prints a thread spec for a bolt ("M6 x 70mm") and says nothing at
// all about the seat, the backrest or the legs — the pieces an assembly in 3D is mostly
// made of. The retailer's dimension drawing is where those numbers are written: 48,2 cm
// across the backrest, 46,8 x 41,5 on the seat, 73,3 overall.
//
// Which pictures those are is already known by the time this runs: the gallery
// classification flags them as it reads each picture's direction, so nothing here has to
// look at the strip again.

// Every dimensioned picture across every submodel, deduplicated, best first.
//
// Across submodels because a dimension drawing is a fact about the PRODUCT and not about
// the colour somebody photographed — the oak version and the walnut version are the same
// chair, and whichever listing happens to carry the drawing carries it for both.
export function dimensionPictures(docId: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const sub of getSubmodels(docId)) {
    sub.gallery.forEach((url, i) => {
      if (!sub.galleryDimensioned[i] || seen.has(url)) return;
      seen.add(url);
      out.push(url);
    });
  }
  return out;
}

// The parts this asks about: everything that is not hardware and not a tool.
//
// The same rule the backend applies, and it is repeated here for one reason — the page has
// to know whether there is anything to ask BEFORE it offers a button. A control that spends
// a model call to discover there was nothing to do is worse than one that is not shown.
export const wantsDims = (p: PartItem) => !isHardwareOrTool(p);

// The exact prompt the model will be sent, asked of the route that builds it.
//
// From the backend and not assembled again here, which is the whole point: a preview that
// drifts from what is actually sent is worse than none — it would be read as the reason
// for an answer it had no part in. The route builds it and stops, without a model call or
// a picture fetch.
// The drawings come back WITH it, and the model that would read them: this is the shape
// every call badge in the app is fed (see estimatePrompt just below, and CallBadge), so the
// pictures sit above the text of the call they belong to instead of being laid out again by
// whoever draws the badge.
export async function dimensionsPrompt(
  docId: string, productName: string,
): Promise<{ prompt: string; urls: string[]; model: string }> {
  const parts = getParts(docId);
  const urls = dimensionPictures(docId);
  const { data } = await axios.post<{ prompt?: string }>(
    '/api/notice/part-dimensions',
    {
      preview: true,
      urls,
      productName,
      parts: parts.map((p) => ({
        name: p.name, nameEn: p.nameEn, description: p.description,
        category: p.category, tool: p.tool,
      })),
    },
  );
  // The pictures are the ones just sent, and not a list read back off the answer: this
  // route echoes no urls, and the run reads the same function for the call itself.
  return { prompt: data.prompt || '', urls, model: RUN_MODEL };
}

export type DimsResult = {
  sized: number;
  asked: number;
  reasoning: string;
  raw: string;
};

// What the model thought and what it actually replied, kept per notice.
//
// Persisted rather than held in the button, for two reasons. The read also happens inside
// "Run phases 1 to 3", where no button was pressed and there is no component to hold the
// answer — a badge that only ever filled after a manual click would be empty in the case
// the chain is meant to make ordinary. And it survives a reload, which is when you come
// back to a number that looks wrong and want to know where it came from.
// AND THE PICTURES IT WAS READ FROM, where there were any. Stored with the answer rather than
// recomputed when the panel opens: what this shows is what THAT run measured, and the crops
// under it can move — a box struck through since would change a fresh reading and must not
// quietly change the account of a reading already made.
//
// The view and the box travel with the URL because the reasoning names them: "left: scale
// 1.241 mm/px y" is a line about one of these photographs and nothing else says which.
// AND WHAT IT GAVE, WRITTEN ON THE EDGES THAT GAVE IT. `bbox` is the rectangle the pixels
// were counted from; `across` and `up` are the two numbers that came out of it, already in the
// words the derivation uses ("y 398 mm"). Formatted here rather than in the panel because they
// are what THAT run read — a picture that has since been re-boxed must not have its old answer
// redrawn on its new rectangle.
export type DimsShot = {
  url: string; view: string; wide: number; tall: number;
  bbox: [number, number, number, number];
  across: string | null;
  up: string | null;
  // WHETHER THE SIZE CAME FROM THIS ONE. Only the biggest box is believed (see bestRead), and
  // the others are kept because they are the working — a strip of two photographs with no mark
  // on either claims both were used.
  used?: boolean;
};
export type DimsThinking = {
  reasoning: string; raw: string; at: string; shots?: DimsShot[];
};

const thinkingKey = (docId: string) => `notice.dims.thinking.${docId}`;

export const getDimsThinking = (docId: string): DimsThinking | null =>
  readState<DimsThinking | null>(docId, thinkingKey(docId), null);

// THE WHOLE PRODUCT'S OVERALL SIZE, off the same drawings in the same call — "73,3 cm" is
// printed beside the chair, not beside any part of it. It is what anchors a mesh of the
// assembled product (ProductModelStep): that mesh is normalised to about one unit across, or
// written in a model's guessed metres, and without a printed number its box can only say
// "?". Per document and not per part, because there is no part it belongs to.
//
// NULL MEANS NEVER READ, and an object of nulls means read and not printed — the chain tells
// the two apart, so a document sized before this was asked gets the one read it is owed.
// The ends are the size read's bottom and top of the width and the depth — see PartSize.
type ProductSize = {
  x: number | null; y: number | null; z: number | null;
  xBottom?: number | null; xTop?: number | null; yBottom?: number | null; yTop?: number | null;
  note: string | null;
};

const productSizeKey = (docId: string) => `notice.dims.product.${docId}`;

// The record as stored, note included — the parts table's product column shows the note.
export const readProductSize = (docId: string) =>
  readState<ProductSize | null>(docId, productSizeKey(docId), null);

// ANCHORED ON THE HEIGHT, where a part is anchored on its width: see Dims.anchor.
export const getProductSize = (docId: string): Dims => {
  const size = readProductSize(docId);
  return size ? {
    x: size.x, y: size.y, z: size.z, anchor: 'z',
    xBottom: size.xBottom ?? null, xTop: size.xTop ?? null,
    yBottom: size.yBottom ?? null, yTop: size.yTop ?? null,
  } : null;
};

// The chain's way in: read the sizes if there is anything to read, and say nothing if
// there is not.
//
// Separate from runDimensions because the button and the chain want opposite things from
// the same failure. The button is a direct instruction and owes an answer — "no dimension
// drawing" is what you need to hear when you just pressed it. The chain passes through
// every notice, and most manuals have no dimension drawing at all; stopping the run for
// that, or reporting it, would be noise on the normal case.
//
// Parts already sized are what makes it cheap to re-run: nothing to do means no call.
export async function runDimensionsIfUseful(
  docId: string, productName: string, model?: string, force = false,
): Promise<void> {
  if (!dimensionPictures(docId).length) return;
  const parts = getParts(docId);
  // ANY axis, not x: one true dimension is a complete answer now, and a part sized only
  // on its height would otherwise look unsized and be re-read on every run.
  // AND THE PRODUCT, where it was never asked: a document read before the product's size was
  // part of the call has every part sized and would otherwise never be read again.
  const missing = parts.filter(
    (p) => wantsDims(p) && p.size?.x == null && p.size?.y == null && p.size?.z == null,
  ).length + (readProductSize(docId) ? 0 : 1);
  // `force` is a press of the phase's own button, which means "do this phase again" — so
  // the sizes are read again whatever is already stored. Without it they could not be
  // refreshed at all once the standalone button was folded into this one: a re-run carries
  // the old sizes forward, so nothing was ever missing and the read was always skipped.
  // The chain keeps the skip; it crosses every notice and re-reading what it already has
  // would be a call per manual for nothing.
  if (!force && !missing) return;
  try {
    await runDimensions(docId, productName, model);
  } catch {
    /* a manual with no readable drawing is the normal case, not a failed run */
  }
}

// Read the dimensions and write them onto the parts.
//
// Indices, not names. The backend answers with each part's position in the list it was
// sent, so the answer lands on the part that was asked about — matching on names would
// break on the two parts a manual calls "Vis" and on anything the model rephrases.
export async function runDimensions(
  docId: string, productName: string, model?: string,
): Promise<DimsResult> {
  const urls = dimensionPictures(docId);
  if (!urls.length) throw new Error('no-pictures');

  const parts = getParts(docId);
  // No early return when no part wants a size: the product's is asked on every read.
  const asked = parts.filter(wantsDims).length;

  const { data } = await axios.post<{
    dims?: Record<string, {
      x: number | null; y: number | null; z: number | null;
      xBottom: number | null; xTop: number | null; yBottom: number | null; yTop: number | null;
      source: 'measured' | 'derived' | null; note: string | null;
    }>;
    // Null when the reply could not be read — the stored size is then left as it was.
    product?: ProductSize | null;
    reasoning?: string;
    raw?: string;
  }>('/api/notice/part-dimensions', {
    urls,
    productName,
    model,
    // The WHOLE list, hardware included. The backend drops what it will not ask about and
    // answers by index into what it was given, so sending a filtered list here would mean
    // two different numberings and a mapping between them to get wrong.
    parts: parts.map((p) => ({
      name: p.name, nameEn: p.nameEn, description: p.description,
      category: p.category, tool: p.tool,
    })),
  });

  writeState(docId, thinkingKey(docId), {
    reasoning: data.reasoning || '',
    raw: data.raw || '',
    at: new Date().toISOString(),
  } satisfies DimsThinking);

  // Replaced whole, like a part's size: a re-read that finds nothing printed erases the old one.
  if (data.product) writeState(docId, productSizeKey(docId), data.product);

  const dims = data.dims || {};
  let sized = 0;
  for (const [index, value] of Object.entries(dims)) {
    const at = Number(index);
    if (!Number.isInteger(at) || at < 0 || at >= parts.length) continue;
    // Replaced, not merged: see setPartSize. A re-read is the manual answering again, and
    // anything it does not print this time was not printed last time either.
    setPartSize(docId, at, value, { replace: true });
    sized += 1;
  }
  return { sized, asked, reasoning: data.reasoning || '', raw: data.raw || '' };
}

// --- the third call: estimate what the other two left blank ---------------------------

// Which pictures the estimate looks at: photographs of the product ASSEMBLED, taken from
// different sides.
//
// Not the dimension drawings — those are the second call's, they show measurements rather
// than parts, and a part is hard to pick out of a line diagram. And distinct views first,
// because a listing strip is mostly the same chair from angles ten degrees apart, and
// three of those are one picture bought three times. `galleryViews` already names the side
// each picture shows; anything unnamed fills the remainder.
// `only` narrows it to ONE submodel, by id. The estimate wants any clear view of the
// product and does not care which colour it is; a crop is cut from a picture and shown
// beside the part, so it has to be the colour on screen — a walnut button cropped into a
// row somebody is reading about the oak one is a wrong answer that looks like a right one.
export function estimatePictures(docId: string, max = 3, only = ''): string[] {
  const seenUrl = new Set<string>();
  const byView = new Map<string, string>();
  const rest: string[] = [];
  for (const sub of getSubmodels(docId)) {
    if (only && sub.id !== only) continue;
    sub.gallery.forEach((url, i) => {
      if (seenUrl.has(url) || sub.galleryDimensioned[i]) return;
      seenUrl.add(url);
      const view = (sub.galleryViews[i] || '').trim().toLowerCase();
      if (view && view !== 'other' && !byView.has(view)) byView.set(view, url);
      else rest.push(url);
    });
  }
  return [...byView.values(), ...rest].slice(0, max);
}

// The measurable rows of one part's CATEGORY that are still empty — what an estimate
// would be for, and nothing else.
//
// It used to ask whether the part had any size at all, which read the size object as a
// single yes-or-no and got two kinds of part wrong. Hardware was excluded outright,
// through wantsDims, on the argument that a bolt's "M6 x 70" says everything — true of a
// bolt and false of a barrel nut, whose M6 says which screw it takes and nothing about the
// barrel. And a part with a spec was counted as answered, so an M6 beside four blank rows
// looked like a part with a size.
//
// The rows a part is described by are its category's, so those are the rows to ask about.
// A designation is never one of them: see measurableKeys.
export const missingMeasurements = (p: PartItem, category = ''): string[] => {
  const keys = measurableKeys(category);
  if (!keys.length) return [];
  // An estimate does not count as an answer, so every row stays askable on a part this
  // call has already answered. The number it produced is a guess off a photograph: worth
  // having, and worth being able to ask for again from another picture or after a better
  // ruler has arrived. A button that vanished on its own output would make the first guess
  // permanent, and it is the one answer here most likely to be wrong.
  if (p.size?.source === 'derived') return [...keys];
  // ALL of them or none, and that is `source` talking rather than caution. It is one field
  // for the whole size object, so a part with a measured width and a guessed depth could
  // be labelled neither: the ~ in the table and the ruler test in the estimate call both
  // read it, and either answer would be a lie about half the row. A part half measured
  // stays measured, and the axes the drawing did not give it stay empty.
  return keys.some((k) => p.size?.[k] != null) ? [] : [...keys];
};

// Whether to offer the button at all.
export const wantsEstimate = (p: PartItem, category = '') =>
  missingMeasurements(p, category).length > 0;

// WHICH OF THE TWO ESTIMATES this part gets. Hardware and tools are sized from the standard
// they were made to; a component is sized against the parts already measured, in photographs.
//
// The route decides this too, from the same ontology, and it is the route's answer that picks
// the prompt — this is here for the one thing the client owns: whether to send photographs at
// all, and whether their absence is a reason not to ask. A barrel nut can be estimated in a
// notice whose gallery was never found.
//
// The category is passed in rather than read off the part, because the DATABASE's category is
// what the table draws and what the call is sent. See PartsTable's partCategory.
export const fromStandard = (p: PartItem | undefined, category = '') =>
  !!p?.tool || groupOfCategory(category || p?.category) !== 'Components';

// What the estimate routes are sent, built once: the WHOLE list, sizes included, and the
// rows wanted on ONE part of it.
//
// The whole list, because the parts NOT being asked about are the ruler — and which of them
// qualifies is still decided server-side, from the sizes travelling here. What singles out
// the part under the button is `wants`: the rows that part is missing, against an empty list
// everywhere else. A part with no rows wanted is a part with no question, so the call is
// about one part without anything being renumbered — the indices the model is shown, and
// answers with, stay indices into this same list.
//
// Which ROWS are wanted is decided here rather than server-side, because it follows the
// part's category and the database is what owns a part's category.
const estimateParts = (parts: PartItem[], categories: string[], at: number) =>
  parts.map((p, i) => ({
    name: p.name, nameEn: p.nameEn, description: p.description,
    category: categories[i] ?? p.category, tool: p.tool, size: p.size,
    wants: i === at ? missingMeasurements(p, categories[i] ?? p.category ?? '') : [],
  }));

// This call's own thinking, under its own key, per PART. It shares nothing with the
// dimension read: two calls writing one key means the second silently replaces the first's
// reasoning, and the badge that read it would explain the wrong answer.
//
// The part index is in the key for that same reason. The estimate answers about one part, so
// one key for the document would leave every other button's badge explaining a number it had
// no part in — and this is the one call whose sentence matters more than its number.
const estimateThinkingKey = (docId: string, at: number) =>
  `notice.estimate.thinking.${docId}.${at}`;

export const getEstimateThinking = (docId: string, at: number): DimsThinking | null =>
  readState<DimsThinking | null>(docId, estimateThinkingKey(docId, at), null);

// The pictures ONE part's estimate is entitled to: the product's photographs for a component,
// and none at all for hardware.
//
// None because they are useless, and that is a claim about the object rather than about the
// pictures: a barrel nut is a few millimetres in a photograph of an assembled chair, there is
// nothing in the frame to hold it against, and what does size it is the standard it was made
// to. Three images bought to be argued with is three images bought for nothing.
const estimatePicturesFor = (docId: string, p: PartItem | undefined, category: string) =>
  (fromStandard(p, category) ? [] : estimatePictures(docId));

// The exact prompt the estimate will send, and the pictures that go with it, asked of the
// route that builds them. From the backend and not assembled again here: a preview that
// has drifted from the real call is worse than none, because it gets read as the reason
// for an answer it had no part in.
export async function estimatePrompt(
  docId: string, at: number, productName: string, categories: string[] = [],
): Promise<{ prompt: string; urls: string[]; model: string }> {
  const parts = getParts(docId);
  const { data } = await axios.post<{ prompt?: string; urls?: string[] }>(
    '/api/notice/part-estimate',
    {
      preview: true,
      urls: estimatePicturesFor(docId, parts[at], categories[at] ?? ''),
      productName,
      parts: estimateParts(parts, categories, at),
    },
  );
  // WHICH MODEL WOULD READ IT, for the badge's top line. Stated here rather than asked
  // of the route, because this is where it is decided: `runEstimate` sends RUN_MODEL, so
  // naming the same constant is the one answer that cannot drift from the call.
  return { prompt: data.prompt || '', urls: data.urls || [], model: RUN_MODEL };
}

// Estimate ONE part: a component against the parts already measured, a piece of hardware
// against the standard it was made to. Which of the two is the route's decision, from the
// ontology; what is decided here is whether any photograph goes with the question.
//
// The part the button was pressed on, and no other. This used to answer the whole list in a
// single call, on the argument that the ruler and the photographs are the same for every part
// and paying for them once beat paying for them five times. That is true about the bill and
// wrong about the button: a control sitting in one part's cell that quietly fills four other
// cells does something nobody pressing it asked for, and the four numbers it writes are
// guesses on parts whose turn had not come. So the cost is per part now, and so is the
// question. Answers land as `derived`, and the button stays for the parts still blank.
export async function runEstimate(
  docId: string, at: number, productName: string, model?: string,
  categories: string[] = [],
): Promise<boolean> {
  const parts = getParts(docId);
  // A COMPONENT IS MEASURED, NOT ASKED ABOUT. Every square-on photograph of the assembled
  // product has a scale — the mean of what the parts already measured imply on it — and this
  // part's box in that picture's pixels times that scale is its size. That is the same
  // proportion a model was being asked to judge by eye, out of a reading nobody could check;
  // done here it is arithmetic, it is free, and its account names every input. See
  // sizeFromScale.
  //
  // Hardware keeps the call, because there is nothing to compute: a barrel nut's size is a
  // fact about the standard it was made to, and no photograph of a chair holds it.
  if (!fromStandard(parts[at], categories[at] ?? '')) {
    const pixels = await measureImages(dimensionPictures(docId).concat(estimatePictures(docId, 99)));
    const read = scaleEstimates(docId, at, pixels);
    const best = bestRead(read);
    const dims = scaledSize(read);
    writeState(docId, estimateThinkingKey(docId, at), {
      reasoning: scaleDerivation(read, dims),
      raw: scaleJson(parts[at]?.nameEn || parts[at]?.name || '?', read, dims),
      at: new Date().toISOString(),
      shots: read.map((r) => ({
        used: r === best,
        url: r.url,
        view: r.view,
        wide: r.wide,
        tall: r.tall,
        bbox: r.bbox,
        across: r.across == null ? null : `${r.axes.across} ${r.across} mm`,
        up: r.up == null ? null : `${r.axes.up} ${r.up} mm`,
      })),
    } satisfies DimsThinking);
    if (dims.x == null && dims.y == null && dims.z == null) return false;
    setPartSize(docId, at, { ...dims, source: 'derived', note: 'read off the photographs' });
    return true;
  }
  const urls = estimatePicturesFor(docId, parts[at], categories[at] ?? '');
  // Only the question that looks at pictures needs pictures. A hardware estimate reads a
  // designation, so it runs in a notice whose gallery was never found — and refusing it for
  // want of a photograph it does not open would be refusing the one part of this list that
  // can be sized without one.
  if (!urls.length && !fromStandard(parts[at], categories[at] ?? '')) {
    throw new Error('no-pictures');
  }

  const { data } = await axios.post<{
    // Only the rows that were asked for come back, so this is a PATCH of a size and not a
    // size: a barrel nut answers a length and a diameter and says nothing about x.
    dims?: Record<string, Partial<PartSize> & {
      source: 'measured' | 'derived' | null; note: string | null;
    }>;
    reasoning?: string;
    raw?: string;
  }>('/api/notice/part-estimate', {
    urls,
    productName,
    model,
    // The indices answered are indices into what was sent, so the whole list goes.
    parts: estimateParts(parts, categories, at),
  });

  writeState(docId, estimateThinkingKey(docId, at), {
    reasoning: data.reasoning || '',
    raw: data.raw || '',
    at: new Date().toISOString(),
  } satisfies DimsThinking);

  // One index is the only one that can come back — the route answers about the parts it was
  // asked about, and this call asked about one. Read it by name rather than looping: a loop
  // over the answer is a loop that would write whatever else appeared in it.
  const value = (data.dims || {})[String(at)];
  if (!value) return false;
  setPartSize(docId, at, value);
  return true;
}
